/**
 * Matches server `generateUniqueFilename` in mediaStorage.js:
 * `{baseName}-{Date.now()}-{Math.round(Math.random() * 1e9)}{ext}`
 * so we can show the original base (e.g. dummy_contacts.xlsx) in the UI when falling back to URL basename.
 * @param {string} basename - decoded filename segment only (no path)
 * @returns {string}
 */
export function stripClusterDenDocumentsStorageUniqueSuffix(basename) {
  if (!basename || typeof basename !== "string") return basename;
  const q = basename.split("?")[0].trim();
  const m = q.match(/^(.+)-(\d{13})-(\d{1,10})(\.[a-zA-Z][a-zA-Z0-9]*)$/);
  if (!m) return basename;
  const base = m[1];
  if (!base || base === "." || base === "..") return basename;
  return base + m[4];
}

/**
 * WhatsApp workspace documents are stored under .../workspaces/.../documents/ with unique suffixes.
 * Strip that suffix for display only (does not affect IG/FB paths under /instagram/ or /facebook/).
 * @param {string} documentUrl
 * @param {string} basenameFromUrl - decoded last path segment
 * @returns {string}
 */
export function stripWhatsAppWorkspaceDocumentUrlBasename(documentUrl, basenameFromUrl) {
  if (typeof documentUrl !== "string" || typeof basenameFromUrl !== "string") return basenameFromUrl;
  const u = documentUrl.toLowerCase();
  if (!u.includes("/workspaces/") || !u.includes("/documents/")) return basenameFromUrl;
  if (/\/instagram\//i.test(documentUrl) || /\/facebook\//i.test(documentUrl)) return basenameFromUrl;
  return stripClusterDenDocumentsStorageUniqueSuffix(basenameFromUrl);
}

/**
 * Human-friendly document title for inbox (not storage URL slug).
 * @param {string} basename - Filename or basename from URL
 * @returns {boolean}
 */
export function looksLikeGeneratedMediaBasename(basename) {
  if (!basename || typeof basename !== "string") return true;
  const s = basename.split("?")[0].trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  if (/^fb-\d+[-\w]+/i.test(s) || /^ig-\d+[-\w]+/i.test(s)) return true;
  if (/^temp-/i.test(s)) return true;
  if (/\.bin$/i.test(lower) && s.length > 12) return true;
  if (/^\d{10,}[-.]/.test(s)) return true;
  return false;
}

/**
 * @param {string} fullName - e.g. "Quarterly Report Q4.pdf"
 * @returns {string} e.g. "Quarterly Report … .pdf"
 */
export function truncateDocumentTitleTwoWords(fullName) {
  if (!fullName || typeof fullName !== "string") return "Document";
  const trimmed = fullName.trim();
  if (!trimmed) return "Document";
  const lastDot = trimmed.lastIndexOf(".");
  const hasExt = lastDot > 0 && lastDot < trimmed.length - 1;
  const extPart = hasExt ? trimmed.slice(lastDot) : "";
  const nameOnly = hasExt ? trimmed.slice(0, lastDot) : trimmed;
  const words = nameOnly.split(/\s+/).filter(Boolean);
  if (words.length <= 2 && nameOnly.length <= 48) {
    return trimmed;
  }
  const head = words.slice(0, 2).join(" ");
  if (!head) {
    return hasExt ? `Document${extPart}` : trimmed.slice(0, 36) + (trimmed.length > 36 ? "…" : "");
  }
  return `${head} …${extPart}`;
}

/**
 * @param {string|File} documentUrl
 * @param {string} [documentDisplayName] - from server (original filename)
 * @param {{ platform?: string }} [options] - when platform is "whatsapp", strip workspace document storage suffix from URL basename
 * @returns {string}
 */
export function resolveDocumentDisplayTitle(documentUrl, documentDisplayName, options = {}) {
  const platform = options.platform;
  if (documentDisplayName && String(documentDisplayName).trim()) {
    let shown = String(documentDisplayName).trim();
    if (platform === "whatsapp" || platform === "qr") {
      // Outgoing/saved docs often carry storage basename even when URL is not under /workspaces/.../documents/
      shown = stripClusterDenDocumentsStorageUniqueSuffix(shown);
      if (typeof documentUrl === "string") {
        shown = stripWhatsAppWorkspaceDocumentUrlBasename(documentUrl, shown);
      }
    }
    return truncateDocumentTitleTwoWords(shown);
  }
  if (documentUrl instanceof File && documentUrl.name) {
    return truncateDocumentTitleTwoWords(documentUrl.name);
  }
  if (typeof documentUrl === "string") {
    const parts = documentUrl.split("/");
    let basename = parts[parts.length - 1] || "document";
    basename = basename.split("?")[0];
    try {
      basename = decodeURIComponent(basename);
    } catch (_) {}
    if (platform === "whatsapp" || platform === "qr") {
      basename = stripWhatsAppWorkspaceDocumentUrlBasename(documentUrl, basename);
    }
    if (looksLikeGeneratedMediaBasename(basename)) {
      const m = basename.match(/\.([a-zA-Z0-9]+)$/);
      const ext = m ? `.${m[1].toLowerCase()}` : "";
      return `Document${ext}`;
    }
    return truncateDocumentTitleTwoWords(basename);
  }
  return "Document";
}
